import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import './ResultPage.css';
import ViewAnswers from './ViewAnswers';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import CountUp from 'react-countup';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use'; // optional, to make it responsive
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { FiClock } from 'react-icons/fi'; // for average time
import { GiFastArrow } from 'react-icons/gi'; // for fastest question
import { GiSnail } from 'react-icons/gi'; // for slowest question
import studentApi from './StudentApi';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';
import "animate.css";


function ResultPage() {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const answersRef = React.useRef(null);
  const {
    questions: initialQuestions,
    selectedAnswers: initialSelectedAnswers,
  } = location.state || {};

  const [showConfetti, setShowConfetti] = useState(true);
  const { width, height } = useWindowSize(); // get dynamic window size
  const [loading, setLoading] = useState(true);
  const [resultData, setResultData] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [showAnswers, setShowAnswers] = useState(false);

  // ✅ Get values sent from the previous screen
  const { questions, selectedAnswers, examId, topicId, startTime, endTime, examType, questionTimes } = location.state || {};


  const examIdFromQuestions = questions?.[0]?.exam_id;
  const finalExamId = examId || examIdFromQuestions;

  // ✅ Fetch data from backend
  useEffect(() => {
    const fetchResult = async () => {
      try {
        const res = await studentApi.get(`/getPracticeResult/${resultId}`);
        console.log("🔍 API response:", res.data);

        if (res.data.success && res.data.result_summary) {
          setResultData(res.data.result_summary);
          setAnswers(res.data.answers || []);
        } else {
          console.warn("⚠️ No result_summary found in response");
          setResultData(null);
        }
      } catch (error) {
        console.error("Error fetching result:", error);
        setResultData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [resultId]);

  // ✅ Derive accuracy & focus area safely using resultData
  const correct = resultData?.correct || resultData?.result_summary?.correct || 0;
  const totalQuestions = resultData?.total_marks || resultData?.result_summary?.total_marks || 0;


  // ✅ Calculate performance metrics (only for practice)
  const practiceStats = useMemo(() => {
    if (examType !== 'practice' || !questionTimes) return null;

    const questionEntries = Object.entries(questionTimes);
    if (!questionEntries.length) return null;

    const times = questionEntries.map(([_, t]) => t);
    const totalSec = times.reduce((a, b) => a + b, 0);
    const avgSec = totalSec / times.length;
    const maxTime = Math.max(...times);
    const minTime = Math.min(...times);

    const slowestQ = questionEntries.find(([_, t]) => t === maxTime)?.[0];
    const fastestQ = questionEntries.find(([_, t]) => t === minTime)?.[0];

    const avgReadable = `${Math.floor(avgSec)} sec`;
    const feedback =
      avgSec > 120
        ? "⏱ You’re taking a bit long per question. Try improving your solving speed."
        : avgSec > 60
          ? "💡 Good pace, but can be a bit faster to maximize efficiency."
          : "🔥 Excellent speed! Keep up this momentum.";



    const accuracy = totalQuestions
      ? Math.round((correct / totalQuestions) * 100)
      : 0;



    let focusArea =
      accuracy < 60
        ? "🎯 Focus on accuracy — review your weak concepts."
        : avgSec > 90
          ? "⚡ Focus on speed — try solving with time limits."
          : "✅ Balanced performance. Keep practicing consistently.";

    const chartData = questionEntries.map(([idx, sec]) => ({
      question: `Q${parseInt(idx) + 1}`,
      time: sec,
    }));

    return {
      avgSec,
      maxTime,
      minTime,
      slowestQ: parseInt(slowestQ) + 1,
      fastestQ: parseInt(fastestQ) + 1,
      feedback,
      focusArea,
      chartData,
      avgReadable,
    };
  }, [examType, questionTimes, correct, totalQuestions]);

  if (loading) return <div className="loading">Loading Result...</div>;
  if (!resultData) return <div>No result found.</div>;

  // Normalize result summary for UI
  const summary = {
    attempted: resultData?.attempted ?? resultData?.result_summary?.attempted ?? 0,
    unattempted: resultData?.unattempted ?? resultData?.result_summary?.unattempted ?? 0,
    correct: resultData?.correct ?? resultData?.result_summary?.correct ?? 0,
    wrong: resultData?.wrong ?? resultData?.result_summary?.wrong ?? 0,
    score_obtained: resultData?.score_obtained ?? resultData?.result_summary?.score_obtained ?? 0,
    total_marks: resultData?.total_marks ?? resultData?.result_summary?.total_marks ?? 0,
  };


  //  Format date and time in readable Indian format
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  //  Pre-format start & end time
  const formattedStartTime = formatDateTime(resultData?.started_at);
  const formattedEndTime = formatDateTime(resultData?.submitted_at);


  const calculateDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate - startDate;
    if (diffMs <= 0) return 'N/A';

    const totalMinutes = Math.floor(diffMs / 60000);
    const totalSeconds = Math.floor((diffMs % 60000) / 1000);

    return `${totalMinutes} min ${totalSeconds} sec`;
  };

  const totalDuration = resultData?.duration_taken
    ? `${resultData.duration_taken} sec`
    : calculateDuration(resultData?.started_at, resultData?.submitted_at);



const handleShareScreenshot = async () => {
  try {
    const element = document.getElementById("screenshot-section");
    if (!element) return;

    const canvas = await html2canvas(element, { scale: 2 });
    const dataURL = canvas.toDataURL("image/png");

    // Download as PNG
    saveAs(dataURL, `Result_${resultId}.png`);

    // 🎉 Use SweetAlert2 instead of alert
    Swal.fire({
      title: "📸 Screenshot Captured!",
      text: "Your result screenshot has been saved. You can now share it with your teacher.",
      icon: "success",
      confirmButtonText: "Great!",
      confirmButtonColor: "#3085d6",
      background: "#f7faff",
      color: "#333",
      showClass: {
        popup: "animate__animated animate__fadeInDown",
      },
      hideClass: {
        popup: "animate__animated animate__fadeOutUp",
      },
    });
  } catch (error) {
    console.error("Error capturing screenshot:", error);

    // ❌ SweetAlert2 error alert
    Swal.fire({
      title: "❌ Failed!",
      text: "Oops... something went wrong while capturing the screenshot. Please try again.",
      icon: "error",
      confirmButtonText: "Retry",
      confirmButtonColor: "#d33",
    });
  }
};


  return (
    <>
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={200}
          gravity={0.2}
          recycle={false} // stops confetti after it falls
          onConfettiComplete={() => setShowConfetti(false)} // optional
        />
      )}

      <div className="result-container"  id="result-page">
        <div id='screenshot-section'>
        {showConfetti && <Confetti width={width} height={height} recycle={false} />}
        <h2 className="result-heading">✅ Exam Submitted Successfully!</h2>
        <p className='result-subtext'>Here's a summary of your performance</p>

        <div className='summary-grid'>
          <div className='summary-item total'>
            <div
              className='summary-title'
              data-tooltip-id="summary-tooltip"
              data-tooltip-content="The total number of questions in this exam."
            >
              📝 Total Questions
            </div>
            <div className='summary-value'>
              <CountUp end={resultData?.total_marks || 0} duration={1.5} />
            </div>
          </div>

          <div className='summary-item attempted'>
            <div
              className='summary-title'
              data-tooltip-id="summary-tooltip"
              data-tooltip-content="Questions you have attempted during the test."
            >
              ✏️ Attempted
            </div>
            <div className='summary-value'>
              <CountUp end={summary.attempted} duration={1.5} />
            </div>
          </div>

          <div className='summary-item unattempted'>
            <div
              className='summary-title'
              data-tooltip-id="summary-tooltip"
              data-tooltip-content="Questions you skipped or left unanswered."
            >
              ⏸ Unattempted
            </div>
            <div className='summary-value'>
              <CountUp end={summary.unattempted} duration={1.5} />
            </div>
          </div>

          <div className='summary-item correct'>
            <div
              className='summary-title'
              data-tooltip-id="summary-tooltip"
              data-tooltip-content="Questions you answered correctly."
            >
              ✅ Correct
            </div>
            <div className='summary-value'>
              <CountUp end={summary.correct} duration={1.5} />
            </div>
          </div>

          <div className='summary-item wrong'>
            <div
              className='summary-title'
              data-tooltip-id="summary-tooltip"
              data-tooltip-content="Questions you answered incorrectly."
            >
              ❌ Wrong
            </div>
            <div className='summary-value'>
              <CountUp end={summary.wrong} duration={1.5} />
            </div>
          </div>

          <div className='summary-item score'>
            <div
              className='summary-title'
              data-tooltip-id="summary-tooltip"
              data-tooltip-content="Your total marks or score obtained in this exam."
            >
              🏆 Score
            </div>
            <div className='summary-value'>
              <CountUp end={summary.score_obtained} duration={1.5} />
              <span className='total-marks'>/ {summary.total_marks}</span>
            </div>
          </div>
        </div>


      {/* 🎯 Exam Set Details Section */}
<div className="exam-set-details-card">
  <h3 className="exam-set-heading">🎯 Exam Set Details</h3>
  <div className="exam-set-grid">
    <div className="exam-set-item">
      <span className="exam-set-label">
        <span className="exam-set-icon practice-icon">📝</span> Practice Set Name
      </span>
      <span className="exam-set-value">{resultData?.practice_set_name || '—'}</span>
    </div>
    <div className="exam-set-item">
      <span className="exam-set-label">
        <span className="exam-set-icon exam-icon">🎯</span> Exam Name
      </span>
      <span className="exam-set-value">{resultData?.exam_name || '—'}</span>
    </div>
    <div className="exam-set-item">
      <span className="exam-set-label">
        <span className="exam-set-icon subject-icon">📚</span> Subject Name
      </span>
      <span className="exam-set-value">{resultData?.subject_name || '—'}</span>
    </div>
    <div className="exam-set-item">
      <span className="exam-set-label">
        <span className="exam-set-icon topic-icon">🔖</span> Topic Name
      </span>
      <span className="exam-set-value">{resultData?.topic_name || '—'}</span>
    </div>
  </div>
</div>

{/* Existing timing section below */}
<div className="result-time-section">
  <h3 className="result-time-heading">🕒 Exam Timing Details</h3>
  <div className="time-grid">
    <div className="time-row">
      <span className="time-left"><span className="time-icon">🕒</span> Start Time:</span>
      <span className="time-right badge">{formattedStartTime}</span>
    </div>
    <div className="time-row">
      <span className="time-left"><span className="time-icon">🏁</span> End Time:</span>
      <span className="time-right badge">{formattedEndTime}</span>
    </div>
    <div className="time-row highlight">
      <span className="time-left"><span className="time-icon">⏱</span> Total Duration:</span>
      <span className="time-right badge duration-badge">{totalDuration}</span>
    </div>
  </div>
</div>
</div>


        {/* ✅ Practice Performance Summary (only for practice) */}
        {examType === 'practice' && practiceStats && (
          <div className="practice-performance">
            <h3 className="practice-heading">📊 Practice Performance Summary</h3>

            <div className="practice-grid">
              <div className="practice-item">
                <FiClock style={{ marginRight: '6px', color: '#007bff' }} />
                <strong>Average Time / Question:</strong> {practiceStats.avgReadable}
              </div>
              <div className="practice-item">
                <GiFastArrow style={{ marginRight: '6px', color: '#28a745' }} />
                <strong>Fastest Question:</strong> Q{practiceStats.fastestQ} ({practiceStats.minTime}s)
              </div>
              <div className="practice-item">
                <GiSnail style={{ marginRight: '6px', color: '#dc3545' }} />
                <strong>Slowest Question:</strong> Q{practiceStats.slowestQ} ({practiceStats.maxTime}s)
              </div>
            </div>

            <div className="feedback-section">
              <p><strong>🧠 Feedback:</strong> {practiceStats.feedback}</p>
              <p><strong>🎯 Focus Area:</strong> {practiceStats.focusArea}</p>
            </div>

            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={practiceStats.chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="question" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="time" barSize={40} fill="#4a90e2" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {showAnswers && (
  <div ref={answersRef}>
    <ViewAnswers
      questions={questions}
      selectedAnswers={selectedAnswers}
    />
  </div>
)}


        <div className="result-buttons">
          <button
            className={`view-answers-btn ${showAnswers ? 'active' : ''}`}
            onClick={() => {
              setShowAnswers(prev => !prev);
              // Scroll to answers section when showing
              if (!showAnswers && answersRef.current) {
                answersRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            {showAnswers ? "Hide Answers" : "View Answers"}
          </button>

          <button
            className='try-again-btn'
            onClick={() => {
              const practiceSetId = location.state?.practiceSetId || questions?.[0]?.practice_set_id;
              if (!practiceSetId) {
                alert("Cannot retry: Practice Set ID missing");
                return;
              }

              navigate(`/test-practice/${practiceSetId}`, {
                state: {
                  questions,        // optional, if you want to pre-fill questions
                  topicId,
                  subjectId: location.state?.subjectId,
                  duration: location.state?.duration, // pass original duration
                }
              });
            }}
          >
            Try Again
          </button>


          <button
            className='dashboard-btn'
            onClick={() => navigate('/dashboard', { state: { fromResultPage: true } })}
          >
            Go to Dashboard
          </button>

<button className='share-screenshot-btn' onClick={handleShareScreenshot}>
  📸 Capture Result Screenshot
</button>

        </div>
      </div>
      <ReactTooltip
        id="summary-tooltip"
        place="top"
        style={{
          backgroundColor: '#007bff',
          color: '#fff',
          borderRadius: '8px',
          fontSize: '0.9rem',
          padding: '8px 12px',
          boxShadow: '0px 4px 10px rgba(0,0,0,0.15)',
        }}
      />
    </>
  );
}

export default ResultPage;
